import React, { useEffect, useState } from "react";
import { Mic2, Plus, MessageSquare, Check, AlertTriangle, HelpCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, KeyBadge, BpmBadge } from "@/components/ui-kit";
import { toast } from "sonner";

const SONG_STATUS = [
  { key: "ready", label: "Ready", icon: Check, cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  { key: "needs_work", label: "Needs work", icon: AlertTriangle, cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  { key: "question", label: "Question", icon: HelpCircle, cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
];

export default function Rehearsal() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const canManage = ["super_admin", "worship_director", "worship_leader"].includes(user?.role);

  const load = async () => {
    const { data } = await api.get("/rehearsals");
    setList(data);
    if (data.length) { const r = await api.get(`/rehearsals/${data[0].id}`); setActive(r.data); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (songId, status) => {
    await api.post(`/rehearsals/${active.id}/song-status`, { song_id: songId, status });
    setActive({ ...active, songs: active.songs.map((s) => (s.song_id === songId ? { ...s, status } : s)) });
  };
  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.post(`/rehearsals/${active.id}/notes`, { text: note });
    setActive({ ...active, notes: [...active.notes, data] });
    setNote(""); toast.success("Note added");
  };

  return (
    <div>
      <PageHeader eyebrow="Prepare together" title="Rehearsal Room"
        subtitle="Work the set, flag what needs attention, and capture the leader's notes." />
      {!active ? (
        <EmptyState icon={Mic2} title="No rehearsals scheduled" body="Create a service and schedule a rehearsal to prepare your set here." />
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="wf-card rounded-2xl p-5 mb-4">
              <div className="eyebrow mb-1">{active.date}</div>
              <h2 className="font-display text-xl font-bold text-[#F7F5F0]">{active.title} · {active.time}</h2>
            </div>
            <div className="space-y-3">
              {active.songs.map((s, i) => (
                <div key={s.song_id + i} className="wf-card rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-xs text-[#6E7D97] font-mono">SONG {i + 1}</div>
                      <div className="font-medium text-[#F7F5F0]">{s.title}</div>
                      <div className="flex gap-2 mt-1.5"><KeyBadge>{s.key}</KeyBadge><BpmBadge>{s.bpm} BPM</BpmBadge></div>
                    </div>
                    <div className="flex gap-2">
                      {SONG_STATUS.map((st) => (
                        <button key={st.key} onClick={() => setStatus(s.song_id, st.key)} data-testid={`song-status-${st.key}`}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${s.status === st.key ? st.cls : "bg-white/5 text-[#8A97AC] border-white/10"}`}>
                          <st.icon className="w-3.5 h-3.5" /> {st.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="wf-card rounded-2xl p-5 h-fit">
            <h3 className="font-display font-semibold text-[#F7F5F0] mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#E5C378]" /> Rehearsal notes</h3>
            <div className="space-y-3 mb-4 max-h-80 overflow-auto">
              {active.notes.length === 0 && <p className="text-sm text-[#6E7D97]">No notes yet.</p>}
              {active.notes.map((n) => (
                <div key={n.id} className="bg-[#0B101D] border border-white/5 rounded-lg p-3">
                  <div className="text-sm text-[#F7F5F0]">{n.text}</div>
                  <div className="text-[11px] text-[#6E7D97] mt-1">{n.author}</div>
                </div>
              ))}
            </div>
            {canManage && (
              <div className="flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="rehearsal-note-input"
                  placeholder="Add a note…" className="flex-1 bg-[#0B101D] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F7F5F0]" />
                <button onClick={addNote} data-testid="add-note-button" className="px-3 rounded-lg wf-gold-btn"><Plus className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
